import type {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  GenerateCaptionRequest,
  GenerateImageRequest,
  CreatePostRequest,
} from '../types';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  }

  // Auth
  async login(data: LoginRequest): Promise<AuthResponse> {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async register(data: RegisterRequest): Promise<AuthResponse> {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMe() {
    return this.request('/auth/me');
  }

  // Social Accounts
  async getSocialAccounts() {
    return this.request('/social/accounts');
  }

  async connectSocial(platform: string) {
    return this.request(`/social/connect/${platform}`);
  }

  async disconnectSocial(accountId: number) {
    return this.request(`/social/accounts/${accountId}`, {
      method: 'DELETE',
    });
  }

  // Media
  async uploadMedia(file: File, save = false) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('save', save.toString());

    const response = await fetch(`${API_BASE}/media/upload`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    return response.json();
  }

  async getMedia(type?: string, savedOnly = false) {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (savedOnly) params.append('saved', 'true');

    return this.request(`/media?${params}`);
  }

  async deleteMedia(id: number) {
    return this.request(`/media/${id}`, { method: 'DELETE' });
  }

  // AI Content
  async generateCaption(data: GenerateCaptionRequest) {
    return this.request('/ai/generate-caption', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async generateHashtags(prompt: string, language = 'en') {
    return this.request('/ai/generate-hashtags', {
      method: 'POST',
      body: JSON.stringify({ prompt, language }),
    });
  }

  async generateImage(data: GenerateImageRequest) {
    return this.request('/ai/generate-image', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async generateCarousel(topic: string, slidesCount = 5, language = 'en') {
    return this.request('/ai/generate-carousel', {
      method: 'POST',
      body: JSON.stringify({ topic, slides_count: slidesCount, language }),
    });
  }

  async generateDailySuggestions() {
    return this.request('/ai/generate-daily-suggestions', {
      method: 'POST',
    });
  }

  async getDailySuggestions(date?: string) {
    const params = date ? `?date=${date}` : '';
    return this.request(`/ai/daily-suggestions${params}`);
  }

  async approveSuggestion(id: number) {
    return this.request(`/ai/daily-suggestions/${id}/approve`, {
      method: 'POST',
    });
  }

  // Posts
  async createPost(data: CreatePostRequest) {
    return this.request('/posts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getPosts(status?: string, platform?: string) {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (platform) params.append('platform', platform);

    return this.request(`/posts?${params}`);
  }

  async getPost(id: number) {
    return this.request(`/posts/${id}`);
  }

  async updatePost(id: number, data: Partial<CreatePostRequest>) {
    return this.request(`/posts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePost(id: number) {
    return this.request(`/posts/${id}`, { method: 'DELETE' });
  }

  async publishPost(id: number) {
    return this.request(`/posts/${id}/publish`, { method: 'POST' });
  }

  async getCalendar(year: number, month: number) {
    return this.request(`/posts/calendar/${year}/${month}`);
  }

  // Analytics
  async getAnalyticsOverview(days = 30) {
    return this.request(`/analytics/overview?days=${days}`);
  }

  async getPostAnalytics(postId: number) {
    return this.request(`/analytics/posts/${postId}`);
  }

  async refreshAnalytics() {
    return this.request('/analytics/refresh', { method: 'POST' });
  }

  async getAnalyticsTrends(days = 7) {
    return this.request(`/analytics/trends?days=${days}`);
  }

  // Automation
  async getAutomationSettings() {
    return this.request('/automation/settings');
  }

  async updateAutomationSettings(data: any) {
    return this.request('/automation/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getBusinessProfile() {
    return this.request('/automation/profile');
  }

  async updateBusinessProfile(data: any) {
    return this.request('/automation/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async uploadLogo(file: File) {
    const formData = new FormData();
    formData.append('logo', file);

    const response = await fetch(`${API_BASE}/automation/profile/logo`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Logo upload failed');
    }

    return response.json();
  }

  // AI Generators
  async generateFacebookAd(data: { product: string; target_audience?: string; objective?: string }) {
    return this.request('/generators/facebook-ad', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async generateNewsletter(data: { topic: string; tone?: string; sections?: number }) {
    return this.request('/generators/newsletter', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async generateInstagramAd(data: { product: string; style?: string; target_audience?: string }) {
    return this.request('/generators/instagram-ad', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async generateUGC(data: { product: string; ugc_type?: string; persona?: string }) {
    return this.request('/generators/ugc', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async generateTikTokVideo(data: { topic: string; duration?: number; trend?: string }) {
    return this.request('/generators/tiktok-video', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async generateAd(data: { product: string; platform?: string; format?: string; objective?: string }) {
    return this.request('/generators/ad', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async generateInfluencerCampaign(data: { product: string; niche?: string; budget_range?: string }) {
    return this.request('/generators/influencer-campaign', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }
}

export const api = new ApiClient();
